import React, { useState, useEffect } from 'react';
import VideoPoster from '../images/hero/video-poster.jpg';
import scrollTo from 'gatsby-plugin-smoothscroll';

type Props = {
	heroPage: string;
	HeroImage?: string;
	headline: string;
	subHeadline: string;
	showVideo?: boolean;
	videoLink?: string | string[];
	showBackgroundVideo?: boolean;
};

const VideoMp4 = 'https://static.truthfinder.com/pc-office-video-3-8.mp4';
const VideoOgv = 'https://static.truthfinder.com/pc-office-video-3-8.ogv';

const Hero = ({ heroPage, HeroImage, headline, subHeadline, showVideo, videoLink, showBackgroundVideo }: Props) => {
	const currentPath = typeof window !== 'undefined' ? window.location.pathname : '/';

	let subPage = false;
	if (currentPath.includes('/people-search') || currentPath.includes('/customer-care') || currentPath.includes('/classmates') || currentPath.includes('/omniwatch')) {
		subPage = true;
	}

	// State for video links
	const [videoLinks, setVideoLinks] = useState<string[]>([]);
	const [currentVideoLink, setCurrentVideoLink] = useState<string>('');
	const [videoIndex, setVideoIndex] = useState(0);

	// Fisher-Yates shuffle algorithm
	const shuffleArray = (array: string[]) => {
		const newArray = [...array];
		for (let i = newArray.length - 1; i > 0; i--) {
			const j = Math.floor(Math.random() * (i + 1));
			[newArray[i], newArray[j]] = [newArray[j], newArray[i]];
		}
		return newArray;
	};

	// Shuffle array or single link
	useEffect(() => {
		if (!videoLink) return;

		// Handle both string and string[] types
		if (Array.isArray(videoLink)) {
			const shuffled = shuffleArray(videoLink);
			setVideoLinks(shuffled);
			setCurrentVideoLink(shuffled[0]);
			setVideoIndex(0);
		} else if (typeof videoLink === 'string') {
			setVideoLinks([videoLink]);
			setCurrentVideoLink(videoLink);
			setVideoIndex(0);
		}
	}, [videoLink]);

	// Update current video when showVideo changes
	useEffect(() => {
		if (showVideo && videoLinks.length > 1) {
			setCurrentVideoLink(videoLinks[videoIndex]);
		}
	}, [showVideo, videoLinks, videoIndex]);

	return (
		<div className={subPage ? 'hero-wrap' : 'hero-wrap-nosub'}>
			<div id="hero" className={`${heroPage} ${subPage ? 'subHeight' : ''}`} style={HeroImage ? { background: `url(${HeroImage})` } : undefined}>
				{showBackgroundVideo ? (
					<div className="background-video">
						<video playsInline autoPlay muted loop poster={VideoPoster} id="video-bg">
							<source src={VideoMp4} type="video/mp4" />
							<source src={VideoOgv} type="video/ogv" />
						</video>
					</div>
				) : (
					''
				)}
				<div className="container-1200">
					<div className="intro wrap">
						<div>
							<h1
								dangerouslySetInnerHTML={{
									__html: headline
								}}
							></h1>
							<h2>{subHeadline}</h2>
						</div>
					</div>
					{showVideo && (
						<div className="video wrap">
							<div>
								<p className="tooltip">Play video to learn more!</p>
								<div>
									<iframe
										width="480"
										height="270"
										src={currentVideoLink}
										title="video"
										frameBorder="0"
										allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
										allowFullScreen
									></iframe>
								</div>
								{videoLinks.length > 1 && (
									<div className="video-controls">
										<div className="video-indicators">
											{videoLinks.map((_x: string, id: number) => (
												<div
													key={id}
													className={`indicator-dot ${id === videoIndex ? 'active' : ''}`}
													onClick={() => {
														setVideoIndex(id);
														setCurrentVideoLink(videoLinks[id]);
													}}
												/>
											))}
										</div>
									</div>
								)}
							</div>
						</div>
					)}
					<div className="down-arrow" onClick={() => scrollTo('.about')}>
						<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48">
							<g id="Group_17725" data-name="Group 17725" transform="translate(-496 -762)">
								<g id="arrow_downward-24px" transform="translate(508 774)">
									<path id="Path_6313" data-name="Path 6313" d="M0,0H24V24H0Z" fill="none" />
									<path id="Path_6314" data-name="Path 6314" d="M20,12l-1.41-1.41L13,16.17V4H11V16.17L5.42,10.58,4,12l8,8Z" fill="#fff" />
								</g>
								<g id="Ellipse_17" data-name="Ellipse 17" transform="translate(496 762)" fill="none" stroke="#fff" strokeWidth="2">
									<circle cx="24" cy="24" r="24" stroke="none" />
									<circle cx="24" cy="24" r="23" fill="none" />
								</g>
							</g>
						</svg>
					</div>
				</div>
			</div>
		</div>
	);
};

export default Hero;
